/**
 * 受注管理（C-04）と受注詳細（C-03）が**共有する表示部品**（M1.6）。
 *
 * ここに集めるのは、FR-130（LINE 未達）と FR-131（配送料）が どちらも
 * 「一覧で気づいて、詳細で確かめる」という同じ導線に載る事実だからで、
 * **語彙と足し算を2画面に別々に書くと、卸は画面ごとに違う結論を持つ**（どちらが正しいか
 * 卸には判断できない）。判定そのものはサーバが配る（`line_alert`）＝画面は描くだけ。
 */

import type { Money as MoneyValue } from "@kenzai/api-client";
import { Badge, Money } from "@kenzai/ui";

/* ------------------------------------------------------------------ */
/* FR-130・D-89：LINE 未達                                             */
/* ------------------------------------------------------------------ */

/**
 * 未達チップの説明文（05 §6.1 C-03/C-04＝一覧と詳細で同一）。
 *
 * ★**断定しない**＝こちらから分かるのは「送れなかった／送る相手がいない／ブロックされている」
 *   までで、相手が読んだかどうかは分からない。だから「可能性があります」と書く。
 * ★**代わりにやることを書く**（お電話等）＝状態だけ出しても、卸は次の一手を持てない。
 */
export const LINE_ALERT_TEXT =
  "LINEが届いていない可能性があります（ブロック・送信失敗）。お電話等でご連絡ください";

/**
 * 未達チップ。**文字は短く（「LINE未達」）・説明は `title` と詳細画面の帯**に置く。
 *
 * 表の行に全文を並べると、既に8〜9列ある一覧（T-D25 で 1280px が限界と確認済み）が崩れる。
 */
export function LineAlertChip({ testId, title = LINE_ALERT_TEXT }: { testId?: string; title?: string }) {
  return (
    // `title` を差し替えられるようにしてあるのは接続一覧（C-20）のため＝そこでは
    // 「ブロック」と「友だち追加がまだ」で**打つべき手が違う**（電話／追加のご案内）。
    <span data-testid={testId} title={title}>
      <Badge tone="warn">LINE未達</Badge>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* FR-131・D-90：配送料                                                */
/* ------------------------------------------------------------------ */

/**
 * 商品計と配送料を足した「この注文の合計」。
 *
 * ★**サーバの `total_amount` は明細からの導出＝商品計のまま**（FR-82 の規則を変えない）なので、
 *   足し合わせは画面の責務。職人がカート・完了画面で見た額（商品計＋配送料）と同じものを
 *   卸にも出すためにここで足す。
 * ★**商品計が null（単価未確定）なら合計も null**＝配送料だけを合計として出さない
 *   （「500円の注文」に見えてしまう）。
 */
export function orderGrandTotal(itemsTotal: MoneyValue, deliveryFee: MoneyValue): MoneyValue {
  if (itemsTotal === null) return null;
  if (deliveryFee === null) return itemsTotal;
  return String(Number(itemsTotal) + Number(deliveryFee));
}

/**
 * 一覧（C-04）の金額セル＝**列を増やさずに内訳を畳む**（05 §6.1 C-04）。
 *
 * ★**`delivery_fee` が null の卸には何も足さない**＝「配送料 0円」と書くと、別途かかる卸と
 *   見分けが付かないうえ「無料キャンペーン」と読める（込み＝配送料という概念を使っていない、
 *   と 0 円は別物）。
 */
export function OrderAmount({
  itemsTotal,
  deliveryFee,
}: {
  itemsTotal: MoneyValue;
  deliveryFee: MoneyValue;
}) {
  return (
    <>
      <Money value={orderGrandTotal(itemsTotal, deliveryFee)} />
      {deliveryFee !== null && (
        <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          {/* 商品計が出せないときは「うち」と言えない（何の内訳か指す先が無い） */}
          （{itemsTotal === null ? "配送料 " : "うち配送料 "}
          <Money value={deliveryFee} />）
        </div>
      )}
    </>
  );
}

/**
 * 配送料を職人の画面に出すときの文（FR-131・D-90⑤）。
 *
 * C-18 の設定プレビュー（「相手の画面にどう出るか」）と読み合わせる文言はここが正。
 * **統合カート（A-10）側の実装とは別物**＝あちらは注文金額が決まっているので
 * 「あと△△円で無料」まで出せるが、設定画面には注文が無いので条件だけを述べる。
 */
export function deliveryFeePreview(
  mode: "included" | "separate",
  baseFee: string,
  freeThreshold: string | null,
): string {
  if (mode === "included") {
    return "配送料は表示しません（商品価格に含まれているため）。";
  }
  const fee = `配送料 ${Number(baseFee || 0).toLocaleString("ja-JP")}円`;
  if (freeThreshold === null || freeThreshold === "") return `${fee}`;
  return `${fee}（${Number(freeThreshold).toLocaleString("ja-JP")}円以上のご注文で無料）`;
}
